import { Box, Flex, Heading, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import 'borrower/borrower.scss';

import { useGetUserActiveCreditFacilitiesQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { LoanProductSelect } from 'packages/common-frontend/components/LoanProductSelect';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { CreditFacilitySelectWithUrlParams } from 'packages/credit-facility/ui/CreditFacilitySelect';
import { ReceivablesTable } from 'packages/loanProduct/ui/receivablesDashboard/ReceivablesTable';
import { z } from 'zod/v4';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

// Types
const RouteParamsStruct = z.object({
  creditFacilityId: z.string({ message: 'Missing URL parameter' }),
});

// Exports
export const CreditFacilityReceivablesDashboardRoute = () => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const { creditFacilityId: creditFacilityIdParam } = RouteParamsStruct.parse(
    params,
  );

  const [loanProductId, setLoanProductId] = useState<string>('all');

  const [receivablesCount, setReceivablesCount] = useState<number | null>(null);

  const { creditFacilityId, setCreditFacilityId } = useCreditFacilityContext();

  if (creditFacilityIdParam && creditFacilityId !== creditFacilityIdParam) {
    setCreditFacilityId(creditFacilityIdParam);
  }

  let routeLevelError = false;

  const { data: userData } = useGetUserActiveCreditFacilitiesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
    },
  });

  if (!userData || !userData.user) {
    return <Skeleton data-testid="loading-receivables" height="600px" />;
  }

  const {
    user: {
      organizationId,
      organization: { creditFacilities },
    },
  } = userData;

  const creditFacility = _.find(
    creditFacilities,
    ({ id }) => id === creditFacilityId,
  );

  const creditFacilitySlug = creditFacility?.slug;

  if (
    !creditFacilityId ||
    !creditFacilitySlug ||
    !creditFacility.latestCreditAgreementId
  ) {
    navigate('/');
    toast({
      ...errorToastConfig,
      id: 'errorLoadReceivablesDashboardNoCreditFacility',
      title: 'Please select a credit facility.',
    });
    return null;
  }

  return (
    <Box className="main">
      <Sentry.ErrorBoundary
        onError={() => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            routeLevelError = true;
            toast({
              ...errorToastConfig,
              id: 'errorLoadReceivables',
              title: 'Error loading borrower receivables dashboard route',
            });
            navigate('/');
          }
        }}
      >
        <Flex mb="24px" direction="column" w="50%">
          <Heading mb="24px">Receivables</Heading>
          <Box>
            <CreditFacilitySelectWithUrlParams
              onSelectRedirectLink={(selectedCreditFacilityId: string) => {
                return `/credit-facility-receivables/${selectedCreditFacilityId}`;
              }}
            />
          </Box>
        </Flex>
        <Flex justifyContent="space-between" alignItems="center">
          <Box w="50%">
            <LoanProductSelect
              creditFacilityId={creditFacilityId}
              loanProductId={loanProductId}
              setLoanProductId={setLoanProductId}
              useAllOption
            />
          </Box>
          {receivablesCount !== null && (
            <Typography variant="section-subtitle" fontWeight="bold">
              Receivable Count: {receivablesCount}
            </Typography>
          )}
        </Flex>

        <ReceivablesTable
          organizationId={organizationId}
          creditFacilitySlug={creditFacilitySlug}
          creditFacilityId={creditFacilityId}
          loanProductId={loanProductId}
          implementationId={creditFacility.implementationId}
          isLenderPage={false}
          setReceivablesCount={setReceivablesCount}
        />
      </Sentry.ErrorBoundary>
    </Box>
  );
};
